/**
 * @lodex-pro/web-portal/nav/portal-features — what the operator's plan includes
 * (operator portal rework PR 6, migration 0212).
 *
 * The API gates every portal read on the operator's effective features (OP_PORTAL_LOGIN,
 * OP_TICKETS, OP_VOLUMES, ...); /v1/operator-portal/me carries them as `features`. The portal
 * reads the same map so a locked entry and a 403 never disagree.
 *
 * `features` is omitted when the API could not read them. That is UNKNOWN, not "off" and not
 * the tier's template defaults (Session 2's review, L6: a Paelode override can differ from the
 * template either way). While unknown the portal locks nothing and claims nothing; the API still
 * gates every read, so a page the plan lacks answers 403 as usual.
 */
import type { OperatorFeatureId } from '@lodex-pro/shared/features';
import type { OperatorPortalMeResponse } from '@lodex-pro/shared/validation/operator-portal-me';

export type PortalFeatureId = OperatorFeatureId;

/**
 * True only when the plan is KNOWN not to include `id`. Still loading, or features not sent,
 * is unknown: false.
 */
export function portalLacks(
  me: OperatorPortalMeResponse | undefined,
  id: OperatorFeatureId,
): boolean {
  if (me?.features === undefined) return false;
  return me.features[id]?.enabled !== true;
}

/** What the portal says when OP_PORTAL_LOGIN is off (design §7). */
export const PORTAL_OFF_MESSAGE = "Your company's portal isn't switched on. Contact Paelode.";

/** The lock text for an entry the plan does not include. No plan or tier names (§58). */
export const NOT_ON_PLAN = "Not on your company's plan";

/**
 * Operator portal rework PR 9 — the role that manages the company's API keys. The API gates the
 * key routes on MANAGE_OPERATOR_API_KEYS, which only OPERATOR_ADMIN holds; foremen and pumpers
 * get neither the nav entry nor the page.
 */
export const PORTAL_ADMIN_ROLE = 'OPERATOR_ADMIN';

/** True when the signed-in person is their company's portal admin. Unknown reads as no. */
export function portalIsAdmin(me: OperatorPortalMeResponse | undefined): boolean {
  return me?.roleCode === PORTAL_ADMIN_ROLE;
}

/** OPP PR 7 — a foreman runs routes but never tank setup. */
export function portalIsForeman(me: OperatorPortalMeResponse | undefined): boolean {
  return me?.roleCode === 'FOREMAN';
}

/** OPP PR 7 — who may open the Routes page (pumpers never do); Tank setup is the admin's alone. */
export function portalManagesRoutes(me: OperatorPortalMeResponse | undefined): boolean {
  return portalIsAdmin(me) || portalIsForeman(me);
}

/**
 * OPP PR 12a — who may turn a load in to a hauler (SUBMIT_HAUL_REQUEST: pumpers, foremen and
 * the admin). The API checks the permission; this is the UX.
 */
export function portalRequestsHauls(me: OperatorPortalMeResponse | undefined): boolean {
  return (
    me?.roleCode === 'PUMPER' || me?.roleCode === 'FOREMAN' || me?.roleCode === 'OPERATOR_ADMIN'
  );
}

/**
 * OPP PR 12a — haul requests need OP_HAUL_REQUESTS and OP_ROUTES_PUMPERS (the tanks one looks
 * after are what one can request for), the same features the API gates on.
 */
export function portalHaulRequestsOnPlan(me: OperatorPortalMeResponse | undefined): boolean {
  return !portalLacks(me, 'OP_HAUL_REQUESTS') && !portalLacks(me, 'OP_ROUTES_PUMPERS');
}
